import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  VStack,
  Text,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import React from 'react';

import {
  Badge,
  DropdownInput,
  DropdownOption,
  ModulesEnableOptions,
  SettingsOutline,
} from '@aslanpay/portal-common-lib';

import { getGlobalAllowancePercentageOptions } from 'app/components/PayCalendars/options';

import { MODULE_DISPLAY_INFO, formatModuleName } from '../types';

const STATUS_SELECT_OPTIONS: DropdownOption[] = [
  { value: 'ENABLED', text: 'Enabled' },
  { value: 'DISABLED', text: 'Disabled' },
  { value: 'UNAVAILABLE', text: 'Unavailable' },
];

export interface ModuleElementProps {
  module: string;
  isChecked: boolean;
  isForm?: boolean;
  isDisabled?: boolean;
  isEditMode?: boolean;
  onCheckboxClick?: () => void;
  onGlobalAllowancePercentageValueSet?: (value: string | number) => void;
  onConfigure?: () => void;
  onStatusSelect?: (status: ModulesEnableOptions) => void;
  onStatusChange?: (status: ModulesEnableOptions) => void;
  status?: ModulesEnableOptions;
  supportsProvisioning?: boolean;
  additionalContent?: React.ReactNode;
}

function Module({
  module,
  isChecked,
  isForm = false,
  isDisabled = false,
  isEditMode = false,
  onCheckboxClick = () => {},
  onGlobalAllowancePercentageValueSet = () => {},
  onConfigure = undefined,
  onStatusSelect = undefined,
  onStatusChange = undefined,
  status = undefined,
  supportsProvisioning = false,
  additionalContent = undefined,
}: ModuleElementProps): React.ReactElement {
  const displayInfo = MODULE_DISPLAY_INFO[module];
  const title = displayInfo?.title ?? formatModuleName(module);
  const description = displayInfo?.description ?? '';
  const [globalAllowancePercentageValue, setGlobalAllowancePercentageValue] = React.useState<
    number | string | undefined
  >(undefined);

  const globalAllowancePercentageOptions = getGlobalAllowancePercentageOptions();

  const isModuleToggleAllowed = status !== 'UNAVAILABLE' || !supportsProvisioning;
  const isCardClickable = !status && !isDisabled && (!isEditMode || (!isChecked && isEditMode));

  const handleClick = () => {
    if (onCheckboxClick && !isDisabled && isModuleToggleAllowed) {
      onCheckboxClick();
    }
  };

  const statusMeta = {
    ENABLED: { label: 'Enabled', badgeStatus: 'active' },
    DISABLED: {
      label: supportsProvisioning ? 'Provisioned — not live' : 'Disabled',
      badgeStatus: 'warning',
    },
    UNAVAILABLE: {
      label: supportsProvisioning ? 'Not provisioned' : 'Unavailable',
      badgeStatus: 'locked',
    },
  } as const;
  const isCreateCompanyStatusSelector = Boolean(onStatusChange);
  const createCompanyStatusOptions: Array<{ value: ModulesEnableOptions; label: string }> =
    supportsProvisioning
      ? [
          { value: 'UNAVAILABLE', label: 'None' },
          { value: 'DISABLED', label: 'Provisioned' },
          { value: 'ENABLED', label: 'Enabled' },
        ]
      : [
          { value: 'UNAVAILABLE', label: 'None' },
          { value: 'ENABLED', label: 'Enabled' },
        ];

  const dropdownChange = (value: string | number) => {
    setGlobalAllowancePercentageValue(value);
    if (onGlobalAllowancePercentageValueSet) {
      onGlobalAllowancePercentageValueSet(value);
    }
  };

  return (
    <Box
      bg="brand.lightGray2"
      borderRadius={3}
      border="1px solid"
      borderColor="brand.lightGray1"
      p={isCreateCompanyStatusSelector ? '12px' : '20px'}
      width="100%"
      onClick={isCardClickable ? handleClick : undefined}
      _hover={
        isForm && isCardClickable
          ? {
              cursor: 'pointer',
              backgroundColor: 'brand.lightPurple',
              borderColor: 'brand.purpleAlpha30',
            }
          : undefined
      }
    >
      <HStack
        justify="start"
        alignItems="flex-start"
        spacing={isCreateCompanyStatusSelector ? 0 : 2}
      >
        <VStack
          align="start"
          width="100%"
          spacing={isCreateCompanyStatusSelector ? '8px' : undefined}
        >
          <Text onClick={isChecked && isEditMode ? handleClick : undefined} textStyle="h5SemiBold">
            {title}
          </Text>
          <Text
            onClick={isChecked && isEditMode ? handleClick : undefined}
            marginTop="3px !important"
            textStyle="extraSmallMedium"
          >
            {description}
          </Text>

          {status && !isCreateCompanyStatusSelector && (
            <Badge
              value={statusMeta[status].label}
              status={statusMeta[status].badgeStatus}
              customContainerStyles={{ mt: '6px !important' }}
            />
          )}

          {!isCreateCompanyStatusSelector && additionalContent}

          {module === 'RESERVE' && isForm && isChecked && !isDisabled && isEditMode && (
            <FormControl isInvalid={false} isRequired pt="20px">
              <FormLabel>Set and spend allowance (%) </FormLabel>
              <DropdownInput
                placeholder="Users can set and spend up to this limit"
                defaultValue={globalAllowancePercentageOptions.find(
                  (option) => option.value === globalAllowancePercentageValue
                )}
                options={globalAllowancePercentageOptions}
                onChange={dropdownChange}
                onBlur={() => {}}
                style={{ backgroundColor: 'white', borderRadius: '10px' }}
                data-testid={`module-${module}-global-allowance-percentage`}
              />
              {/* <FormErrorMessage>{errors?.odpAllowancePercentage?.message}</FormErrorMessage> */}
            </FormControl>
          )}

          {onStatusChange && (
            <ButtonGroup size="xs" isAttached variant="outline" width="100%">
              {createCompanyStatusOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={status === option.value ? 'solid' : 'outline'}
                  isDisabled={isDisabled}
                  flex={1}
                  onClick={(event) => {
                    event.stopPropagation();
                    onStatusChange(option.value);
                  }}
                  data-testid={`set-module-${module}-${option.value}`}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
          )}
        </VStack>

        <HStack alignSelf="center" flexShrink={0}>
          {!onStatusChange && onStatusSelect && status && (
            <Box minW="200px">
              <DropdownInput
                isDisabled={isDisabled}
                options={STATUS_SELECT_OPTIONS}
                defaultValue={STATUS_SELECT_OPTIONS.find((option) => option.value === status)}
                onChange={(value) => onStatusSelect(value as ModulesEnableOptions)}
                onBlur={() => {}}
                style={{ backgroundColor: 'white', borderRadius: '10px' }}
                data-testid={`module-status-${module}`}
              />
            </Box>
          )}

          {!onStatusChange && onConfigure && (
            <Button
              leftIcon={<SettingsOutline boxSize="18px" />}
              variant="outline"
              size="sm"
              onClick={onConfigure}
              data-testid={`configure-module-${module}`}
            >
              Configure
            </Button>
          )}
        </HStack>
      </HStack>
    </Box>
  );
}

export default Module;
